/**
 * LiveTimelineStrip — docked time/live control at the base of the map.
 *
 * Shown only when the Live layer is enabled in the app's mapper config.
 * Docked (not floating): rendered as a strip inside the map column footer.
 * Reuses the existing live polling (LiveObjectsLayer writes livePositions) and
 * exposes the trail window from config; selecting a window is local UI state for
 * now (LiveFeedAdapter.trail consumes it when trails are rendered on the map).
 */

import { useState } from 'react';
import { Radio, Clock } from 'lucide-react';
import { useMapStore } from '../MapContext';
import { useMapperPluginConfig } from '../mapperConfig';

const TRAIL_WINDOWS = ['1h', '6h', '24h', '7d'];

export default function LiveTimelineStrip() {
  const { data: configData } = useMapperPluginConfig();
  const liveConfig = configData?.config?.layers?.live;
  const liveEnabled = Boolean(liveConfig?.enabled);
  const defaultWindow = liveConfig?.trail_window || '24h';
  const [trailWindow, setTrailWindow] = useState(defaultWindow);
  const { livePositions } = useMapStore();

  if (!liveEnabled) return null;

  const activeCount = livePositions.filter((p) => p.lat != null && p.lng != null).length;

  return (
    <div className="z-20 flex-shrink-0 border-t border-gray-200 bg-white/95 px-3 py-1.5 backdrop-blur">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
          </span>
          <Radio className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-xs font-medium text-gray-700">
            Live
            <span className="ml-1 text-gray-400">
              {activeCount > 0 ? `· ${activeCount} active` : '· waiting for positions'}
            </span>
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-gray-400" />
          <div className="flex items-center gap-0.5">
            {TRAIL_WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setTrailWindow(w)}
                className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                  trailWindow === w
                    ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
